import { UserUpdateInput } from 'dtos/UserUpdateInput';
import {
  IReactionDisposer,
  makeAutoObservable,
  reaction,
  runInAction,
} from 'mobx';
import { AuthStore } from 'stores/AuthStore';
import { createForm } from 'forms/editUserForm';
import { Plan, Product, Role } from 'dtos/common';
import { UserListStore } from 'stores/UserListStore';
import { Match } from 'navigo';
import { RouteName } from 'stores/RootStore';
import * as moment from 'moment';
import { fieldValueToMoment } from 'forms/utils/muiFormBindings';
import { UserAdminOutput } from 'dtos/UserAdminOutput';
import { difference, without } from 'lodash';
import { getFeatureFlagValue } from '@bowery-valuation/feature-flagger-client';
import { defaultProducts, featureFlags } from 'utils/config';

export class EditUserStore {
  private dispose: IReactionDisposer[] = [];
  private authStore: AuthStore;
  private userListStore: UserListStore;
  private setRoute: (route: RouteName) => void;

  form = createForm();
  message = '';
  hasError: boolean = false;
  editingUser: UserAdminOutput | null = null;
  prevProducts: Product[] = [];

  constructor({
    authStore,
    userListStore,
    setRoute,
  }: {
    authStore: AuthStore;
    userListStore: UserListStore;
    setRoute: (route: RouteName) => void;
  }) {
    this.authStore = authStore;
    this.userListStore = userListStore;
    this.setRoute = setRoute;
    makeAutoObservable(this, {}, { autoBind: true });

    this.dispose.push(
      reaction(
        () => this.form.$('planProducts').get('value'),
        (products: Product[]) => {
          const justChecked = difference(products, this.prevProducts)[0];
          if (justChecked === Product.WORLD) {
            this.form.$('planProducts').set('value', [Product.WORLD]);
          } else if (justChecked !== undefined) {
            this.form
              .$('planProducts')
              .set('value', without(products, Product.WORLD));
          }
          this.prevProducts = products;
        },
      ),
    );
  }

  destroy() {
    this.dispose.map((fn) => fn());
  }

  async activate(match: Partial<Match>) {
    this.message = '';
    this.hasError = false;
    this.editingUser = null;
    // TODO: avoid loading twice
    if (!this.userListStore.users.length) {
      await this.userListStore.getUsers();
    }
    const username = match?.data?.username;
    if (!username) {
      this.setRoute('search');
      return;
    }
    const foundUser = this.userListStore.users.find(
      (user) => user.username === username,
    );
    if (!foundUser) {
      this.setRoute('search');
      return;
    }
    this.updateFormWithUser(foundUser);
  }

  async updateFormWithUser(user: UserAdminOutput) {
    this.editingUser = user;
    const {
      email,
      plan,
      planExpiration,
      planRenews,
      planProducts,
      paymentCustomerId,
      roles,
    } = this.editingUser;
    this.prevProducts = planProducts || [];
    this.form.set('value', {
      password: '',
      confirmPassword: '',
      email,
      plan: plan || 'none',
      planExpiration: planExpiration ? moment.utc(planExpiration).unix() : '',
      planRenews: !!planRenews,
      planProducts: planProducts || [],
      paymentCustomerId,
      enabled: roles.includes(Role.USER),
      admin: roles.includes(Role.ADMIN),
    });
    await this.form.validate({ showErrors: true });
  }

  get canEditRoles() {
    return this.authStore.currentUser?.username !== this.editingUser?.username;
  }

  async editUser() {
    this.message = '';
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid || !this.editingUser) {
      return;
    }
    const dto = this.formToDto(this.form.values(), this.editingUser);
    try {
      const response = await this.authStore.fetchWithAuth(
        `${process.env.REACT_APP_SERVER_URI}/users`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dto),
        },
      );
      if (!response.ok) throw new Error('Error updating user');
      runInAction(() => {
        this.message = 'The user was updated.';
        this.hasError = false;
      });
    } catch (err) {
      runInAction(() => {
        this.message = 'Unable to update user.';
        this.hasError = true;
      });
    }
  }

  private formToDto(form: any, user: UserAdminOutput): UserUpdateInput {
    const { username } = user;
    const {
      password,
      email,
      plan,
      planExpiration,
      planRenews,
      planProducts,
      paymentCustomerId,
      enabled,
      admin,
    } = form;
    const editedRoles: Role[] = [];
    if (enabled) editedRoles.push(Role.USER);
    if (admin) editedRoles.push(Role.ADMIN);
    const roles = this.canEditRoles
      ? editedRoles
      : this.authStore.currentUser?.roles || [];
    const planExpirationMoment = fieldValueToMoment(planExpiration);
    let products = getFeatureFlagValue(featureFlags.multipleProducts)
      ? planProducts
      : defaultProducts;
    const dto: UserUpdateInput = {
      username,
      password,
      email,
      roles,
      plan: plan === 'none' ? null : plan,
      planExpiration:
        plan !== 'none' && planExpirationMoment
          ? planExpirationMoment.toISOString()
          : null,
      planRenews: plan === Plan.MONTHLY ? planRenews : false,
      planProducts: plan !== 'none' ? products : [],
      paymentCustomerId,
    };
    return dto;
  }
}
